import { getLogger } from '../../logging/logger';

const logger = getLogger('Panel');

/**
 * @typedef {Object} PanelConfig
 * @property {string} [fg] - Foreground color
 * @property {string} [bg] - Background color
 * @property {number} [PR] - Pixel ratio
 * @property {number} [width] - Width
 * @property {number} [height] - Height
 * @property {string} [font] - Font family
 * @property {string} [textBaseline] - Text baseline
 * @property {number} [min] - Minimum value
 * @property {number} [max] - Maximum value
 */
const CONFIG = Object.freeze({
	fg: '#0ff',
	bg: '#002',
	font: 'bold Helvetica,Arial,sans-serif',
	textBaseline: 'top',
	min: Infinity,
	max: 0,
	PR: Math.round(window.devicePixelRatio || 1),
	// Canvas dimension constants
	width: 80,
	height: 48,
	textOffsetX: 3,
	textOffsetY: 2,
	graphOffsetX: 3,
	graphOffsetY: 15,
	graphWidthOffset: 74,
	graphHeightOffset: 30,
	fontSize: 9,
});

/**
 * Performance monitoring panel that displays statistics in a canvas
 */
export default class Panel {
	/**
	 * @param {string} name - Panel name/label
	 * @param {PanelConfig} [config] - Panel configuration
	 */
	constructor(name, config) {
		/** @type {string} */
		this.name = name;

		// Initialize properties from CONFIG and config
		/** @type {string} */
		this.fg = CONFIG.fg;
		/** @type {string} */
		this.bg = CONFIG.bg;
		/** @type {string} */
		this.font = CONFIG.font;
		/** @type {string} */
		this.textBaseline = CONFIG.textBaseline;
		/** @type {number} */
		this.min = CONFIG.min;
		/** @type {number} */
		this.max = CONFIG.max;
		/** @type {number} */
		this.PR = CONFIG.PR;

		// Apply config overrides
		Object.assign(this, config);

		// Canvas dimensions (set in updateDimensions)
		/** @type {number} */
		this.WIDTH = 0;
		/** @type {number} */
		this.HEIGHT = 0;
		/** @type {number} */
		this.TEXT_X = 0;
		/** @type {number} */
		this.TEXT_Y = 0;
		/** @type {number} */
		this.GRAPH_X = 0;
		/** @type {number} */
		this.GRAPH_Y = 0;
		/** @type {number} */
		this.GRAPH_WIDTH = 0;
		/** @type {number} */
		this.GRAPH_HEIGHT = 0;

		// Canvas elements (set in setupCanvas)
		/** @type {HTMLCanvasElement} */
		this.canvas = document.createElement('canvas');
		/** @type {CanvasRenderingContext2D} */
		this.context = /** @type {CanvasRenderingContext2D} */ (this.canvas.getContext('2d'));

		this.updateDimensions();
		this.setupCanvas();
		this.drawInitialCanvas();

		/** @type {HTMLCanvasElement} */
		this.dom = this.canvas;
	}

	/**
	 * Update panel dimensions based on pixel ratio
	 */
	updateDimensions() {
		this.WIDTH = /** @type {number} */ (CONFIG.width) * this.PR;
		this.HEIGHT = /** @type {number} */ (CONFIG.height) * this.PR;
		this.TEXT_X = /** @type {number} */ (CONFIG.textOffsetX) * this.PR;
		this.TEXT_Y = /** @type {number} */ (CONFIG.textOffsetY) * this.PR;
		this.GRAPH_X = /** @type {number} */ (CONFIG.graphOffsetX) * this.PR;
		this.GRAPH_Y = /** @type {number} */ (CONFIG.graphOffsetY) * this.PR;
		this.GRAPH_WIDTH = /** @type {number} */ (CONFIG.graphWidthOffset) * this.PR;
		this.GRAPH_HEIGHT = /** @type {number} */ (CONFIG.graphHeightOffset) * this.PR;
	}

	/**
	 * Setup canvas element and context
	 */
	setupCanvas() {
		// Set canvas class name if not already set
		if (!this.canvas.classList.contains(this.name)) {
			this.canvas.classList.add(this.name);
		}

		this.canvas.width = /** @type {number} */ (this.WIDTH);
		this.canvas.height = /** @type {number} */ (this.HEIGHT);

		this.context.font = `bold ${Math.round(CONFIG.fontSize * this.PR)}px ${this.font}`;
		this.context.textBaseline = CONFIG.textBaseline;
	}

	/**
	 * Draw initial canvas background and labels
	 */
	drawInitialCanvas() {
		this.context.fillStyle = this.bg;
		this.context.fillRect(0, 0, this.WIDTH, this.HEIGHT);

		this.context.fillStyle = this.fg;
		this.context.fillText(this.name, this.TEXT_X, this.TEXT_Y);
		this.context.fillRect(this.GRAPH_X, this.GRAPH_Y, this.GRAPH_WIDTH, this.GRAPH_HEIGHT);

		this.context.fillStyle = this.bg;
		this.context.globalAlpha = 0.9;
		this.context.fillRect(this.GRAPH_X, this.GRAPH_Y, this.GRAPH_WIDTH, this.GRAPH_HEIGHT);
		this.context.globalAlpha = 1;
	}

	/**
	 * Resize panel while preserving graph history
	 * @param {number} width - New width
	 * @param {number} height - New height
	 * @param {number} PR - New pixel ratio
	 */
	resize(width, height, PR) {
		logger.debug('[resize]', { width, height, PR });

		// Save current graph area as image data before resize
		const oldGraphImageData = this.context.getImageData(
			this.GRAPH_X,
			this.GRAPH_Y,
			this.GRAPH_WIDTH,
			this.GRAPH_HEIGHT,
		);

		// Store old dimensions for scaling
		const oldGraphWidth = this.GRAPH_WIDTH;
		const oldGraphHeight = this.GRAPH_HEIGHT;

		// Update pixel ratio and dimensions
		this.PR = PR;
		this.updateDimensions();

		// Create temporary canvas to scale the old graph data
		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = oldGraphWidth;
		tempCanvas.height = oldGraphHeight;
		const tempContext = /** @type {CanvasRenderingContext2D} */ (tempCanvas.getContext('2d'));
		tempContext.putImageData(oldGraphImageData, 0, 0);

		// Setup canvas with new dimensions
		this.setupCanvas();
		this.drawInitialCanvas();

		// Scale and draw the old graph data to new dimensions
		if (oldGraphWidth > 0 && oldGraphHeight > 0) {
			this.context.drawImage(
				tempCanvas,
				0,
				0,
				oldGraphWidth,
				oldGraphHeight,
				this.GRAPH_X,
				this.GRAPH_Y,
				this.GRAPH_WIDTH,
				this.GRAPH_HEIGHT,
			);
		}

		// Don't reset min/max - preserve the scale
	}

	/**
	 * Update panel with new performance value
	 * @param {number} value - Current performance value
	 * @param {number} maxValue - Maximum expected value for scaling
	 */
	update(value, maxValue) {
		logger.debug('[update]', { value, maxValue });

		this.min = Math.min(this.min, value);
		this.max = Math.max(this.max, value);

		this.context.fillStyle = this.bg;
		this.context.globalAlpha = 1;
		this.context.fillRect(0, 0, this.WIDTH, this.GRAPH_Y);
		this.context.fillStyle = this.fg;
		this.context.fillText(
			`${Math.round(value)} ${this.name} (${Math.round(this.min)}-${Math.round(this.max)})`,
			this.TEXT_X,
			this.TEXT_Y,
		);

		this.context.drawImage(
			this.canvas,
			this.GRAPH_X + this.PR,
			this.GRAPH_Y,
			this.GRAPH_WIDTH - this.PR,
			this.GRAPH_HEIGHT,
			this.GRAPH_X,
			this.GRAPH_Y,
			this.GRAPH_WIDTH - this.PR,
			this.GRAPH_HEIGHT,
		);

		this.context.fillRect(
			this.GRAPH_X + this.GRAPH_WIDTH - this.PR,
			this.GRAPH_Y,
			this.PR,
			this.GRAPH_HEIGHT,
		);

		this.context.fillStyle = this.bg;
		this.context.globalAlpha = 0.9;
		this.context.fillRect(
			this.GRAPH_X + this.GRAPH_WIDTH - this.PR,
			this.GRAPH_Y,
			this.PR,
			Math.round((1 - value / maxValue) * this.GRAPH_HEIGHT),
		);
	}
}
